import { FC, Fragment, useState } from 'react';
import { Outbound } from 'ts/flight/interfaces/FlightDetailsResponse';
import { getElapsedStr, getStopsText } from 'utils/flight/flight';
import Tooltip from 'components/ui/Tooltip/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCartFlatbedSuitcase,
  faClock,
  faPersonWalkingLuggage,
  faPlane,
  faSuitcaseRolling,
} from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { getLogoUrl } from 'utils/utils';
import SegmentDetails from './SegmentDetails';
import TicketBaggagePopup from '../Result/TicketBaggagePopup';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { dateFormat } from 'utils/shared/utils';
import { blobUrl } from 'constants/generic';

interface TicketDetailsProps {
  type: string;
  bound: Outbound;
  flightSource: string;
}

const TicketDetails: FC<TicketDetailsProps> = ({ bound, type = 'departure', flightSource }) => {
  const [t] = useTranslation();
  
  const [showDetails, setShowDetails] = useState(false);

  const isRedHandbag =
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag === 0;
  const isRedTrolley =
    !bound.listOfFlyBaggagePrice ||
    !bound.listOfFlyBaggagePrice?.[0] ||
    bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley === 0;
  const isGreenTotal =
    bound.listOfFlyBaggagePrice &&
    (bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight > 0 ||
      bound.listOfFlyBaggagePrice?.[0]?.pieces > 0);

  return (
    <div className="position-relative">
      <div className="default-shadow flight-details-item flight-details-ticket">
        <div className="ticket-source ticket-source-dyn">{t(flightSource)}</div>
        <div className="title-header-grey">
          <span>
            {type === 'departure' && (
              <span>
                <FontAwesomeIcon className="text-sm mr-5 text-contrast" icon={faPlane as any} />{' '}
                {t('flight.departure')}
              </span>
            )}
            {type === 'return' && (
              <span>
                <FontAwesomeIcon className="text-sm mr-5 text-contrast" icon={faPlane as any} />{' '}
                {t('flight.return')}
              </span>
            )}
          </span>
        </div>
        <div className="ticket-details-info">
          <div className="item">
            <span className="flex-center">
              <img src={getLogoUrl(blobUrl, bound.airline.code)} alt={bound.airline.desc} />
              <span className="day">{bound.airline.code}</span>
            </span>
          </div>
          <div className="item">
            <span className="city text-bold">{bound.departureAirport.cityDesc}</span>
            <span className="time text-bold">{dateFormat(bound.departureDateTime, 'HH:mm')}</span>
            <span className="day">
              {dateFormat(bound.departureDateTime, 'EEE - dd MMMM, yyyy')}
            </span>
          </div>
          <div className="item">
            <span className="city text-bold">{bound.arrivalAirport.cityDesc}</span>
            <span className="time text-bold">{dateFormat(bound.arrivalDateTime, 'HH:mm')}</span>
            <span className="day">{dateFormat(bound.arrivalDateTime, 'EEE - dd MMMM, yyyy')}</span>
          </div>
          <div className="item">
            <Tooltip
              children={<FontAwesomeIcon icon={faClock as any} />}
              content={t('filter.duration')}
            />
            <span className="time text-bold">{getElapsedStr(t, bound.elapsedDTime)}</span>
            <span className="day">{getStopsText(t, bound)}</span>
          </div>
        </div>
        <div>
          {bound.gdsFlyAirs.map((gds, index) => (
            <Fragment key={index}>
              <SegmentDetails gds={gds} />
              {index !== bound.gdsFlyAirs.length - 1 && (
                <div className="divider text-sm flex-between">
                  <span>
                    <span className="text-bold">
                      {t('filter.stop_up')} {t('at')} {gds.arrivalAirport.code}{' '}
                    </span>
                    <span>
                      {gds.arrivalAirport.desc} ({gds.arrivalAirport.countryDesc})
                    </span>
                  </span>
                  <span>
                    {getElapsedStr(t, bound.gdsFlyAirs[index + 1].connDTime)}
                    <FontAwesomeIcon icon={faClock as any} className="ml-5" />
                  </span>
                </div>
              )}
            </Fragment>
          ))}
        </div>

        {bound.operatingAirline.code !== bound.airline.code && (
          <div className="position-relative" style={{ top: -15 }}>
            <span className="flight-operating">
              {bound.operatingAirline.code}{' '}
              {t('flight.operated_by') +
                ' ' +
                (bound.operatingAirline.desc || bound.operatingAirline.code)}
            </span>
          </div>
        )}
        <div
          className="position-relative"
          onMouseEnter={() => setShowDetails(true)}
          onMouseLeave={() => setShowDetails(false)}
        >
          {showDetails && <TicketBaggagePopup outbond={bound} />}
          {bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag !== -1 && (
            <span className="mr-10">
              <FontAwesomeIcon
                icon={faSuitcaseRolling as any}
                className={'mr-5 ' + (isRedHandbag ? 'color-danger' : 'text-success')}
              />
              {isRedHandbag ? (
                <span className="color-danger">{t('flight.handbag_excluded')}</span>
              ) : (
                <span className="text-success">
                  {t('flight.handbag_included')}

                  {!!bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag && (
                    <span>
                      {' '}
                      {bound.listOfFlyBaggagePrice?.[0]?.maxWeightHandbag} {t('kg')}
                    </span>
                  )}
                </span>
              )}
            </span>
          )}

          {bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley !== -1 && (
            <span className="mr-10">
              <FontAwesomeIcon
                icon={faPersonWalkingLuggage as any}
                className={'mr-5 ' + (isRedTrolley ? 'color-danger' : 'text-success')}
              />
              {isRedTrolley ? (
                <span className="color-danger">{t('flight.trolley_excluded')}</span>
              ) : (
                <span className="text-success">
                  {t('flight.trolley_included')}

                  {!!bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley && (
                    <span>
                      {' '}
                      {bound.listOfFlyBaggagePrice?.[0]?.maxWeightTrolley} {t('kg')}
                    </span>
                  )}
                </span>
              )}
            </span>
          )}
          {bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight !== -1 && (
            <span className="mr-10">
              <FontAwesomeIcon
                icon={faCartFlatbedSuitcase as any}
                className={'mr-5 ' + (isGreenTotal ? 'text-success' : 'color-danger')}
              />
              {isGreenTotal ? (
                <span className="text-success">
                  {t('flight.bag_included')}

                  {!!bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight && (
                    <span>
                      {' '}
                      {bound.listOfFlyBaggagePrice?.[0]?.maxTotalWeight}
                      {t('kg')}
                    </span>
                  )}
                </span>
              ) : (
                <span className="color-danger">{t('flight.bag_excluded')}</span>
              )}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

export default withErrorBoundary(TicketDetails, 'TicketDetails');
